import { useCallback, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ENUM_ORDER_PAYMENT_STATUS, useGetOrder, useGetOrderLog, useMutateOrderCancel } from 'queries/orders.query';
import {
  Badge,
  BlockStack,
  Box,
  Button,
  DataTable,
  Divider,
  Grid,
  InlineGrid,
  InlineStack,
  LegacyCard,
  Link,
  Page,
  ResourceItem,
  ResourceList,
  SkeletonBodyText,
  SkeletonDisplayText,
  Text,
} from '@shopify/polaris';
import { Helmet } from 'react-helmet-async';
import { BillIcon, CheckIcon, XSmallIcon } from '@shopify/polaris-icons';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleDollarToSlot, faCommentSms, faTicket } from '@fortawesome/free-solid-svg-icons';
import EditOrderTaxModal from 'entities/ticket-managers/components/editOrderTax.modal';
import { getLatestOrderTransaction, getOrderDebitStatus, getOrderPaymentStatus, getOrderStatus } from './order.util';
import __helpers from 'helpers/index';
import EditOrderModal from './edit_order.modal';
import { ENUM_TRANSACTION_PAYMENT_METHOD } from 'queries/transactions.query';
import dayjs from '../../utils/dayjs';
import CustomerDetailPopup from 'components/customer.detail.popup';
import PaymentOrderModal from './payment_order.modal';
import OrderInvoicePreviewModal, { OrderInvoicePreviewData } from 'entities/invoices/components/OrderInvoicePreviewModal';
import { useMutateExportOrderInvoices, useMutatePreviewOrderInvoices } from 'queries/invoices.query';
import { useNotification } from 'context/NotificationContext';

export default function OrderDetail() {
  let { order_id } = useParams();
  const navigate = useNavigate();

  const { data: entity, refetch: reloadOrderData, isLoading: loadingOrderDetail } = useGetOrder(order_id);
  const { data: orderLogData, isLoading: loadingOrderLog } = useGetOrderLog({
    order_id: order_id,
    sort: 'createdAt:desc',
  });
  const { mutateAsync: orderCancel, isPending: cancelling, isSuccess } = useMutateOrderCancel();
  const { addNotification } = useNotification();
  const { mutateAsync: previewOrderInvoice, isPending: previewingInvoice } = useMutatePreviewOrderInvoices();
  const { mutateAsync: exportOrderInvoice, isPending: exportingInvoice } = useMutateExportOrderInvoices();
  const [invoicePreview, setInvoicePreview] = useState<OrderInvoicePreviewData | null>(null);
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);

  const previewInvoice = useCallback(async () => {
    if (!order_id) return;
    try {
      const preview = await previewOrderInvoice(order_id);
      setInvoicePreview(preview);
      setShowInvoicePreview(true);
    } catch (error) {
      addNotification('error', 'Không thể tải thông tin xem trước hóa đơn.');
    }
  }, [addNotification, order_id, previewOrderInvoice]);

  const exportInvoice = useCallback(async () => {
    if (!order_id) return;
    try {
      await exportOrderInvoice(order_id);
      addNotification('info', 'Xuất hóa đơn thành công!');
      setShowInvoicePreview(false);
      setInvoicePreview(null);
      reloadOrderData();
    } catch (error) {
      addNotification('error', 'Không thể xuất hóa đơn. Vui lòng thử lại.');
    }
  }, [addNotification, exportOrderInvoice, order_id, reloadOrderData]);
  // const { mutateAsync: updateOrder, isPending: updating, isSuccess: updateSuccess } = useMutateUpdateOrder();

  const orderCancelCallback = useCallback(async () => {
    let result = window.confirm('Bạn có thực sự muốn hủy đơn này và các vé liên quan?');
    try {
      if (result) await orderCancel(order_id);
    } catch (e) {}
  }, [order_id]);

  /** Edit thông tin thuế */
  const currentOrderForEditingTax = useRef(null);
  const [showEditTaxModal, setShowEditTaxModal] = useState(false);
  const editTaxButtonCallbackFinal = useCallback(async (order_id: string) => {
    currentOrderForEditingTax.current = order_id;
    setShowEditTaxModal(true);
  }, []);
  // new one

  /** edit thông tin vé */
  const [showEditOrderModal, setShowEditOrderModal] = useState(false);
  const [showCustomerPopup, setShowCustomerPopup] = useState(null);
  /** modal thanh toán vé */
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const pdfUrl = process.env.REACT_APP_BACKEND_URL + `/invoices/view_pdf/${entity?.invoices?.maTraCuu}`;
  const latestTransaction = getLatestOrderTransaction(entity?.transactions);

  let trip_date_reformat = dayjs(new Date(Number(entity?.departure_date))).format('YYYY-MM-DD');
  const trip_url = `/ticket_managers?d=${trip_date_reformat}&journey_id=${entity?.journey_id}&h=${entity?.trip_manifest?.time_id}`;

  return (
    <>
      <Helmet>
        <title>Chi tiết đơn hàng</title>
      </Helmet>
      <Page
        backAction={{
          content: 'Quay lại danh sách',
          onAction: () => navigate('/orders'),
        }}
        title={`Đơn hàng ${entity?.order_code}`}
        subtitle={`Ngày đặt đơn ${dayjs(new Date(Number(entity?.createdAt))).format('HH:mm DD/MM/YYYY')}`}
        compactTitle
        titleMetadata={
          <InlineStack gap={'200'}>
            {latestTransaction?.payment_method &&
            ![ENUM_TRANSACTION_PAYMENT_METHOD.BANK, ENUM_TRANSACTION_PAYMENT_METHOD.CASH].includes(latestTransaction.payment_method)
              ? getOrderDebitStatus(latestTransaction.payment_method)
              : null}
            {getOrderPaymentStatus(entity?.payment_status)}
            {entity?.invoices ? (
              <Badge tone="success" icon={CheckIcon}>
                INVOICE
              </Badge>
            ) : (
              <Badge tone="attention" icon={XSmallIcon}>
                INVOICE
              </Badge>
            )}
            {getOrderStatus(entity?.order_status)}
          </InlineStack>
        }
        primaryAction={{
          content: 'Hủy đơn',
          icon: XSmallIcon,
          disabled: cancelling,
          onAction: () => orderCancelCallback(),
        }}
      >
        {loadingOrderDetail ? <SkeletonBodyText /> : null}

        <Grid columns={{ sm: 3 }}>
          <Grid.Cell columnSpan={{ xs: 6, sm: 4, md: 4, lg: 8, xl: 8 }}>
            <BlockStack gap="400">
              <ResourceList
                items={[
                  {
                    id: '100',
                    name: 'Ngày đi',
                    value: dayjs(new Date(Number(entity?.departure_date))).format('DD/MM/YYYY'),
                  },
                  {
                    id: '101',
                    name: 'Chuyến',
                    value: entity?.trip_manifest ? (
                      <Link url={trip_url + `#trip_manifest:${entity?.trip_manifest_id}`}>
                        {entity?.trip_manifest?.departure_hour +
                          ' - ' +
                          dayjs(new Date(Number(entity?.trip_manifest?.departure_date))).format('DD/MM/YYYY')}
                      </Link>
                    ) : (
                      '--'
                    ),
                  },
                  {
                    id: '200',
                    name: 'Hành trình',
                    value: `${entity?.journey?.journey_from} - ${entity?.journey?.journey_to}`,
                  },
                  {
                    id: '300',
                    name: 'Ghi chú đơn hàng',
                    value: entity?.note || '--',
                  },
                  {
                    id: '400',
                    name: 'Tổng thanh toán',
                    value: <strong>{__helpers.formatNumberWithCommas(entity?.total_must_pay)} VND</strong>,
                  },
                  {
                    id: '401',
                    name: 'Đã thanh toán',
                    value: <strong>{__helpers.formatNumberWithCommas(entity?.total_paid || 0)} VND</strong>,
                  },
                  {
                    id: '500',
                    name: 'Hóa đơn',
                    value: (
                      <strong>
                        {entity?.invoices ? (
                          <Link url={pdfUrl} external target="_blank">
                            {`Mã: ${entity?.invoices?.maTraCuu} - Số Hóa đơn: ${entity?.invoices?.shDon}`}
                          </Link>
                        ) : (
                          <Button icon={BillIcon} variant="plain" tone="critical" loading={previewingInvoice} onClick={previewInvoice}>
                            Xuất hóa đơn
                          </Button>
                        )}
                      </strong>
                    ),
                  },
                ]}
                renderItem={(item) => {
                  const { id, name, value } = item;

                  return (
                    <ResourceItem id={id} url={null}>
                      <InlineStack align="space-between">
                        <Text variant="bodyMd" fontWeight="bold" as="h3">
                          {name}
                        </Text>
                        <div>{value}</div>
                      </InlineStack>
                    </ResourceItem>
                  );
                }}
              />
              <br />
              <LegacyCard>
                <LegacyCard.Header
                  title="Thông tin hành khách"
                  actions={[
                    {
                      content: 'Chỉnh sửa',
                      onAction: () => setShowEditOrderModal(true),
                    },
                  ]}
                ></LegacyCard.Header>
                <LegacyCard.Section>
                  {entity ? (
                    <BlockStack gap="200">
                      {/* <Link onClick={() => setShowCustomerPopup(entity?.customer_id)}> */}
                      <Link onClick={() => navigate(`/customer/view/${entity?.customer_id}`)}>
                        <p>
                          {entity?.customer_data?.display_name}{' '}
                          {entity?.customer_data?.user_nickname ? `/ biệt danh ${entity?.customer_data?.user_nickname} / ` : ' / '}
                          {entity?.customer_data?.user_phonenumber}
                        </p>
                      </Link>
                      <Divider />

                      <InlineGrid columns={['oneHalf', 'oneHalf']} gap="400" alignItems="start">
                        <BlockStack gap="200">
                          <Text as="h3" fontWeight="bold">
                            Đón tại
                          </Text>
                          <Text as="p">{entity?.pickup_address}</Text>
                          {entity?.pickup_note && (
                            <Text as="p" tone="critical">
                              {entity?.pickup_note}
                            </Text>
                          )}
                          <Text as="h3" fontWeight="bold">
                            Tài xế đón
                          </Text>
                          <Text as="p" tone="critical">
                            {entity?.order_meta?.pickup_by_data ? (
                              <Text as="span">
                                {entity?.order_meta?.pickup_by_data?.display_name} lúc{' '}
                                {dayjs(Number(entity?.order_meta?.pickup_at)).tz('Asia/Ho_Chi_Minh').format('HH:mm DD-MM-YYYY')}
                              </Text>
                            ) : (
                              '--'
                            )}
                          </Text>
                        </BlockStack>
                        <BlockStack gap="200">
                          <Text as="h3" fontWeight="bold">
                            Xuống tại
                          </Text>
                          <Text as="p">{entity?.dropoff_address}</Text>
                          {entity?.dropoff_note && (
                            <Text as="p" tone="critical">
                              {entity?.dropoff_note}
                            </Text>
                          )}
                          <Text as="h3" fontWeight="bold">
                            Tài xế trả
                          </Text>
                          <Text as="p" tone="critical">
                            {entity?.order_meta?.dropoff_by_data ? (
                              <Text as="span">
                                {entity?.order_meta?.dropoff_by_data?.display_name} lúc{' '}
                                {dayjs(Number(entity?.order_meta?.dropoff_at)).tz('Asia/Ho_Chi_Minh').format('HH:mm DD-MM-YYYY')}
                              </Text>
                            ) : (
                              '--'
                            )}
                          </Text>
                        </BlockStack>
                      </InlineGrid>

                      {entity?.pickup_contact_name ? (
                        <>
                          <Divider />
                          <div>
                            Thông tin đón: {entity?.pickup_contact_name} - {entity?.pickup_contact_phonenumber}
                          </div>
                        </>
                      ) : null}

                      {entity?.dropoff_contact_name ? (
                        <>
                          <Divider />
                          <div>
                            Thông tin trả: {entity?.dropoff_contact_name} - {entity?.dropoff_contact_phonenumber}
                          </div>
                        </>
                      ) : null}
                    </BlockStack>
                  ) : (
                    <Text as="p" tone="subdued">
                      Chưa có thông tin
                    </Text>
                  )}
                </LegacyCard.Section>
              </LegacyCard>
              <LegacyCard title="Thông tin vé" sectioned>
                <DataTable
                  hideScrollIndicator
                  hasZebraStripingOnData
                  columnContentTypes={['text', 'text', 'numeric', 'numeric', 'numeric']}
                  headings={['Số vé', 'Loại vé', 'Giá', 'K.mại', 'Ghế']}
                  rows={
                    entity?.ticket
                      ? entity?.ticket.map((item) => {
                          const { ticket_id, ticket_type, price, discount, markAsDeleted, seat_position } = item;
                          const url = trip_url + `#${entity?.id}`;

                          return [
                            markAsDeleted === 0 ? (
                              <Link url={url}>
                                <FontAwesomeIcon icon={faTicket} /> {ticket_id}
                              </Link>
                            ) : (
                              <span>
                                <FontAwesomeIcon icon={faTicket} /> {ticket_id}
                              </span>
                            ),
                            ticket_type,
                            <div>{__helpers.formatNumberWithCommas(price)}</div>,
                            <div>{__helpers.formatNumberWithCommas(discount)}</div>,
                            <div>{seat_position || '----'}</div>,
                          ];
                        })
                      : []
                  }
                />

                <Divider />
                <br />
                <Text as="h3" variant="headingMd" alignment="end" fontWeight="bold">
                  Tổng vé: {__helpers.formatNumberWithCommas(entity?.total_seat)}
                </Text>
              </LegacyCard>
              <LegacyCard title="Phí đơn hàng" sectioned>
                <ResourceList
                  resourceName={{ singular: 'phí', plural: 'phí' }}
                  items={entity?.order_fee || []}
                  emptyState={<p>Chưa có khoản phí nào!</p>}
                  renderItem={(item) => {
                    return (
                      <ResourceItem
                        verticalAlignment="center"
                        id={`order_fee_` + item.fee_value}
                        url={null}
                        media={<FontAwesomeIcon icon={faCircleDollarToSlot} />}
                        accessibilityLabel={``}
                        //   name={name}
                      >
                        <InlineStack align="space-between" blockAlign="center">
                          <Text variant="bodyMd" fontWeight="bold" as="h3">
                            {item.fee_description}
                          </Text>
                          <div>{__helpers.formatNumberWithCommas(item.fee_value)}</div>
                        </InlineStack>
                      </ResourceItem>
                    );
                  }}
                />
                <Divider />
                <br />
                <Text as="h3" variant="headingMd" alignment="end" fontWeight="bold">
                  Tổng phí: {__helpers.formatNumberWithCommas(entity?.total_fee)}
                </Text>
              </LegacyCard>

              <LegacyCard>
                <LegacyCard.Header
                  title="Thông tin thanh toán"
                  actions={[
                    {
                      content: 'Thanh toán',
                      onAction: () => setShowPaymentModal(true),
                    },
                  ]}
                ></LegacyCard.Header>
                <LegacyCard.Section>
                  {entity?.transactions?.length ? (
                    <ResourceList
                      resourceName={{ singular: 'thanh toán', plural: 'thanh toán' }}
                      items={entity.transactions}
                      renderItem={(item) => {
                        return (
                          <ResourceItem
                            verticalAlignment="center"
                            id={`order_transaction_` + item.id}
                            url={null}
                            // media={<FontAwesomeIcon icon={faCircleDollarToSlot} />}
                            accessibilityLabel={``}
                            //   name={name}
                          >
                            <InlineStack align="space-between" blockAlign="center" wrap={true} gap={'400'}>
                              <Text variant="bodyMd" fontWeight="bold" as="h3">
                                {item.payment_method}
                              </Text>
                              <div>{__helpers.formatNumberWithCommas(item.amount)} đ</div>

                              <Text as="p">
                                <Text as="span" breakWord={true}>
                                  {item.casher_data?.display_name}
                                </Text>
                                <Text as="span" breakWord={true} truncate>
                                  {item.description}
                                </Text>
                              </Text>

                              <Text as="p">{dayjs(Number(item.createdAt)).tz('Asia/Ho_Chi_Minh').format('HH:mm DD-MM-YYYY')}</Text>
                            </InlineStack>
                          </ResourceItem>
                        );
                      }}
                    />
                  ) : (
                    <Text as="p" tone="subdued">
                      Chưa có thông tin thanh toán!
                    </Text>
                  )}
                </LegacyCard.Section>
              </LegacyCard>

              <LegacyCard>
                <LegacyCard.Header
                  title="Thông tin xuất thuế"
                  actions={[
                    {
                      content: 'Chỉnh sửa',
                      onAction: () => editTaxButtonCallbackFinal(entity.id),
                    },
                  ]}
                ></LegacyCard.Header>
                <LegacyCard.Section>
                  {entity?.order_invoice_info ? (
                    <BlockStack gap="400">
                      <div>Loại thuế: {entity?.order_invoice_info?.tax_type}</div>
                      <div>Mã số thuế: {entity?.order_invoice_info?.tax_code}</div>
                      <div>Mã quan hệ ngân sách: {entity?.order_invoice_info?.tax_mamoiquanhengansach || '-'}</div>
                      <div>Người mua: {entity?.order_invoice_info?.name}</div>
                      <div>Tên người mua: {entity?.order_invoice_info?.buyer_name}</div>
                      <div>Phone: {entity?.order_invoice_info?.invoice_phone}</div>
                      <div>Email: {entity?.order_invoice_info?.invoice_email}</div>
                      <div>Address: {entity?.order_invoice_info?.invoice_address}</div>
                    </BlockStack>
                  ) : (
                    <Text as="p" tone="subdued">
                      Xuất thuế theo thông tin khách lẻ mặc định
                    </Text>
                  )}
                </LegacyCard.Section>
              </LegacyCard>
            </BlockStack>
          </Grid.Cell>
          <Grid.Cell columnSpan={{ xs: 6, sm: 2, md: 2, lg: 4, xl: 4 }}>
            <LegacyCard title="Logs" sectioned>
              <BlockStack gap="400">
                {loadingOrderLog ? (
                  <SkeletonDisplayText />
                ) : (
                  orderLogData?.map((log) => {
                    return (
                      <Box>
                        <div>
                          <FontAwesomeIcon icon={faCommentSms} />
                          {log.author_data?.display_name} lúc {dayjs(new Date(Number(log.createdAt))).format('HH:mm DD/MM/YYYY')}
                        </div>
                        <div>
                          <Badge tone="magic">{log.action}</Badge>
                        </div>
                        {Object.entries(log.payload || []).map(([field, value]) => (
                          <div key={field}>
                            {field}: {value.old} →{' '}
                            <Text as="span" tone="success" fontWeight="bold">
                              {value.new}
                            </Text>
                          </div>
                        ))}
                      </Box>
                    );
                  })
                )}
              </BlockStack>
            </LegacyCard>
          </Grid.Cell>
        </Grid>
      </Page>
      <br />
      <br />
      <EditOrderTaxModal open={showEditTaxModal} orderId={currentOrderForEditingTax.current} onClose={() => setShowEditTaxModal(false)} />
      <EditOrderModal
        show={showEditOrderModal}
        order_id={order_id}
        onClose={() => {
          reloadOrderData();
          setShowEditOrderModal(false);
        }}
      />
      <CustomerDetailPopup onClose={() => setShowCustomerPopup(0)} show={showCustomerPopup} />

      <PaymentOrderModal show={showPaymentModal} order_id={order_id} onClose={() => setShowPaymentModal(false)} />
      <OrderInvoicePreviewModal
        open={showInvoicePreview}
        data={invoicePreview}
        loading={exportingInvoice}
        onClose={() => setShowInvoicePreview(false)}
        onExport={exportInvoice}
      />
    </>
  );
}

